import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  formatRecipientsInput,
  METRICS_REPORT_WEEKDAYS,
  parseRecipientsInput,
  type MetricsReportDayOfWeek,
  type MetricsReportScheduleDto,
  type MetricsReportScheduleUpsertPayload,
} from "../../metricsReportSchedule";
import {
  formatMetricsReportPeriodPreview,
  metricsReportPeriodOption,
  METRICS_REPORT_PERIOD_OPTIONS,
  type MetricsReportPeriod,
} from "../../metricsReportPeriod";

function formatYmdDisplay(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString("es-CL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatYmdRange(startDate: string, endDate: string): string {
  return `${formatYmdDisplay(startDate)} → ${formatYmdDisplay(endDate)}`;
}

function FormSectionTitle({
  isSidebar,
  inline = false,
  children,
}: {
  isSidebar: boolean;
  inline?: boolean;
  children: React.ReactNode;
}) {
  if (isSidebar) {
    return (
      <Typography
        variant="h3"
        color="text.primary"
        sx={{ mb: inline ? 0 : 1.5 }}
      >
        {children}
      </Typography>
    );
  }
  return (
    <Typography variant="subtitle2" fontWeight={700}>
      {children}
    </Typography>
  );
}

export function scheduleToFormValues(
  schedule: MetricsReportScheduleDto | null,
  defaultRecipients: string,
): {
  enabled: boolean;
  recipientsInput: string;
  dayOfWeek: MetricsReportDayOfWeek;
  reportPeriod: MetricsReportPeriod;
} {
  if (!schedule) {
    return {
      enabled: false,
      recipientsInput: defaultRecipients,
      dayOfWeek: 1,
      reportPeriod: "last_week",
    };
  }
  return {
    enabled: schedule.enabled,
    recipientsInput:
      schedule.recipients.length > 0
        ? formatRecipientsInput(schedule.recipients)
        : defaultRecipients,
    dayOfWeek: schedule.dayOfWeek,
    reportPeriod: schedule.reportPeriod,
  };
}

type MetricsReportEmailFormProps = {
  initialValues: ReturnType<typeof scheduleToFormValues>;
  savedSchedule: MetricsReportScheduleDto | null;
  defaultRecipients: string;
  savePending: boolean;
  errorMessage: string | null;
  onClearError: () => void;
  onSave: (payload: MetricsReportScheduleUpsertPayload) => void;
  layout?: "page" | "sidebar";
};

export const MetricsReportEmailForm: React.FC<MetricsReportEmailFormProps> = ({
  initialValues,
  savedSchedule,
  defaultRecipients,
  savePending,
  errorMessage,
  onClearError,
  onSave,
  layout = "page",
}) => {
  const { t } = useTranslation("miContenido");
  const isSidebar = layout === "sidebar";
  const [enabled, setEnabled] = React.useState(initialValues.enabled);
  const [recipientsInput, setRecipientsInput] = React.useState(
    initialValues.recipientsInput,
  );
  const [dayOfWeek, setDayOfWeek] = React.useState(initialValues.dayOfWeek);
  const [reportPeriod, setReportPeriod] = React.useState(
    initialValues.reportPeriod,
  );

  const buildSavePayload = (): MetricsReportScheduleUpsertPayload => {
    const recipients = parseRecipientsInput(recipientsInput);
    return {
      enabled,
      recipients:
        recipients.length > 0
          ? recipients
          : [defaultRecipients].filter(Boolean),
      dayOfWeek,
      contentBrandFilter: "all",
      reportPeriod,
    };
  };

  const handleSaveSchedule = () => {
    const recipients = parseRecipientsInput(recipientsInput);
    if (enabled && recipients.length === 0) {
      window.alert(t("errors.needRecipient"));
      return;
    }
    onSave(buildSavePayload());
  };

  const periodOption = metricsReportPeriodOption(reportPeriod);
  const periodPreview = React.useMemo(
    () => formatMetricsReportPeriodPreview(reportPeriod, dayOfWeek),
    [reportPeriod, dayOfWeek],
  );
  const sendWeekdayLabel =
    METRICS_REPORT_WEEKDAYS.find((d) => d.value === dayOfWeek)?.label ?? "";
  const periodLabel = formatYmdRange(
    periodPreview.range.startDate,
    periodPreview.range.endDate,
  );

  return (
    <Stack spacing={isSidebar ? 3 : 2.5}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <FormSectionTitle isSidebar={isSidebar} inline>
          {t("reports.email")}
        </FormSectionTitle>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              color="primary"
              disabled={savePending}
            />
          }
          label={
            <Typography variant="body2" fontWeight={500}>
              {t("reports.enableWeekly")}
            </Typography>
          }
          labelPlacement="start"
          sx={{ mr: 0 }}
        />
      </Stack>

      {!savedSchedule ? (
        <Typography variant="body2" color="text.secondary">
          {t("reports.configureHint")}
        </Typography>
      ) : null}

      {enabled ? (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Chip
            size="small"
            label={t("reports.nextSend", {
              weekday: sendWeekdayLabel,
              date: formatYmdDisplay(periodPreview.sendDate),
            })}
            variant="outlined"
          />
          <Chip
            size="small"
            label={t("reports.periodChip", { period: periodLabel })}
            variant="outlined"
          />
        </Stack>
      ) : null}

      {errorMessage ? (
        <Alert severity="error" onClose={onClearError}>
          {errorMessage}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "flex",
          flexDirection: isSidebar ? "column" : { xs: "column", sm: "row" },
          gap: 2,
          alignItems: { sm: "flex-start" },
        }}
      >
        <TextField
          label={t("reports.recipients")}
          value={recipientsInput}
          onChange={(e) => setRecipientsInput(e.target.value)}
          size="small"
          disabled={savePending}
          placeholder={t("reports.recipientsPlaceholder")}
          helperText={t("reports.recipientsHelper")}
          sx={{
            flex: isSidebar ? undefined : { sm: "1 1 50%" },
            minWidth: 0,
            width: "100%",
          }}
        />

        <FormControl
          size="small"
          disabled={savePending}
          sx={{
            flex: isSidebar ? undefined : { sm: "1 1 25%" },
            minWidth: 0,
            width: "100%",
          }}
        >
          <InputLabel id="metrics-report-weekday">{t("reports.sendDay")}</InputLabel>
          <Select
            labelId="metrics-report-weekday"
            label={t("reports.sendDay")}
            value={dayOfWeek}
            onChange={(e) =>
              setDayOfWeek(Number(e.target.value) as MetricsReportDayOfWeek)
            }
          >
            {METRICS_REPORT_WEEKDAYS.map((d) => (
              <MenuItem key={d.value} value={d.value}>
                {d.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          fullWidth
          disabled={savePending}
          sx={{
            flex: isSidebar ? undefined : { sm: "1 1 25%" },
            minWidth: 0,
            width: "100%",
          }}
        >
          <InputLabel id="metrics-report-period">{t("reports.reportPeriod")}</InputLabel>
          <Select
            labelId="metrics-report-period"
            label={t("reports.reportPeriod")}
            value={reportPeriod}
            onChange={(e) =>
              setReportPeriod(e.target.value as MetricsReportPeriod)
            }
          >
            {METRICS_REPORT_PERIOD_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{periodOption.summary}</FormHelperText>
        </FormControl>
      </Box>

      <Button
        variant="contained"
        size="small"
        onClick={handleSaveSchedule}
        disabled={savePending}
        startIcon={
          savePending ? (
            <CircularProgress size={16} color="inherit" />
          ) : undefined
        }
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        {savedSchedule ? t("actions.saveChangesLower") : t("actions.saveSettings")}
      </Button>
    </Stack>
  );
};
